import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Upload, Wand2, Download, ShieldCheck, Heart, Sparkles } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Stardust } from "@/components/Stardust";
import { PackageCard } from "@/components/PackageCard";
import { BRAND } from "@/lib/brand";
import { http, apiError } from "@/lib/api";
import { toast } from "sonner";

const STEPS = [
  { icon: Upload, title: "Upload your image", body: "Share a drawing, digital art, scan or a photo you're authorized to use — of a person, animal or pet." },
  { icon: Wand2, title: "Answer a few fun questions", body: "Name them, pick their powers and personality. Then watch the magic happen." },
  { icon: Download, title: "Pay only to download", body: "Preview your legend first. You're charged only when you download the clean, watermark-free image." },
];

const EXAMPLES = [
  { src: "/examples/portrait.png", pkg: "Legendary Portrait", label: "A polished, high-resolution fantasy portrait of your character — clean and watermark-free after purchase." },
  { src: "/examples/guardian.png", pkg: "Guardian Pack", label: "The portrait plus a character card with personality, backstory and a heroic tagline." },
  { src: "/examples/epic.png", pkg: "Epic Adventure Pack", label: "Everything in Guardian plus a dramatic, cinematic action scene of your hero." },
];

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const [introPrice, setIntroPrice] = useState(null);

  useEffect(() => {
    http
      .get("/config")
      .then((r) => {
        setPackages(r.data.packages || []);
        setIntroPrice(r.data.intro_price);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []);

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <Stardust count={30} />
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-8 sm:px-8 lg:grid-cols-2 lg:pt-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full bg-[#4A235A]/8 px-4 py-2 text-xs font-extrabold uppercase tracking-[0.2em] text-[#4A235A]">
              <Sparkles size={14} /> Turn drawings into legends
            </span>
            <h1 className="mt-5 font-display text-4xl font-black leading-[1.05] tracking-tight text-[#1A0F1C] sm:text-5xl lg:text-6xl">
              Their imagination,
              <br />
              <span className="shimmer-text">transformed into magic.</span>
            </h1>
            <p className="mt-5 max-w-md text-base font-medium leading-relaxed text-[#5C4A61] md:text-lg">
              Upload an image you're authorized to use — a drawing, digital art, scan, or a photo of a person,
              animal or pet — and we'll transform it into a polished fantasy character with a name, a story, and a
              legend all its own.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/create"
                data-testid="hero-start-button"
                className="inline-flex min-h-[56px] items-center gap-2 rounded-full bg-[#F4D03F] px-8 text-base font-extrabold text-[#1A0F1C] transition-transform hover:-translate-y-1"
              >
                <Wand2 size={20} /> Start the magic
              </Link>
              <Link
                to="/order"
                className="inline-flex min-h-[56px] items-center rounded-full border border-[#4A235A]/20 px-6 text-base font-bold text-[#4A235A] transition-colors hover:bg-[#4A235A] hover:text-white"
              >
                Find my order
              </Link>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm font-semibold text-[#5C4A61]">
              <Heart size={16} className="text-[#9B5DE5]" /> You only pay when you fall in love and download.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-tr from-[#9B5DE5]/20 to-[#F4D03F]/20 blur-2xl" />
            <img
              src={BRAND.hero}
              alt="A child's drawing transforming into a fantasy dragon"
              className="relative w-full rounded-[2rem] object-cover shadow-[0_24px_60px_rgba(74,35,90,0.22)] animate-float-slow"
            />
          </motion.div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-[#4A235A]/10 bg-white/60">
        <div className="mx-auto flex max-w-6xl flex-col gap-3 px-5 py-5 sm:flex-row sm:items-center sm:justify-center sm:gap-8 sm:px-8">
          <span className="flex items-center gap-2 text-sm font-bold text-[#4A235A]">
            <ShieldCheck size={18} className="text-[#9B5DE5]" /> Drawings, digital art, scans & photos welcome — you must be authorized to use the image
          </span>
          <span className="flex items-center gap-2 text-sm font-bold text-[#4A235A]">
            <Heart size={18} className="text-[#9B5DE5]" /> Watermarked previews before you buy — pay only to download the clean image
          </span>
        </div>
      </section>

      {/* From image to legend (approved DEMO example) */}
      <section id="examples" className="mx-auto max-w-6xl px-5 py-14 sm:px-8">
        <h2 className="font-display text-2xl font-bold text-[#1A0F1C] sm:text-3xl lg:text-4xl">From image to legend</h2>
        <p className="mt-2 max-w-2xl text-base font-medium text-[#5C4A61]">
          A real example: an original crayon dragon drawing, transformed into a finished Legendary Portrait. These are
          illustrative demo examples of possible output — not a guarantee of an identical result for every upload.
        </p>
        <figure className="mt-8 overflow-hidden rounded-3xl border border-[#4A235A]/10 bg-white shadow-[0_12px_44px_rgba(74,35,90,0.10)]">
          <img
            src="/examples/portrait.png"
            alt="DEMO example: a child's original crayon drawing of a teal dragon on the left, and its finished polished fantasy Legendary Portrait on the right"
            loading="lazy"
            className="w-full object-contain"
          />
          <figcaption className="grid grid-cols-2 border-t border-[#4A235A]/10 text-center text-sm font-bold text-[#4A235A]">
            <span className="px-3 py-3">Original image</span>
            <span className="border-l border-[#4A235A]/10 px-3 py-3">Example transformation</span>
          </figcaption>
        </figure>

        <h3 className="mt-14 font-display text-xl font-bold text-[#1A0F1C] sm:text-2xl">What each package delivers</h3>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {EXAMPLES.map((ex) => (
            <figure key={ex.pkg} className="overflow-hidden rounded-3xl border border-[#4A235A]/10 bg-white shadow-[0_8px_32px_rgba(74,35,90,0.06)]">
              <img
                src={ex.src}
                alt={`DEMO example for the ${ex.pkg}: the same teal dragon character shown as an illustrative example`}
                loading="lazy"
                className="w-full object-contain"
              />
              <figcaption className="p-4">
                <p className="font-display text-lg font-bold text-[#4A235A]">{ex.pkg}</p>
                <p className="mt-1 text-sm font-medium text-[#5C4A61]">{ex.label}</p>
                <p className="mt-2 text-xs font-semibold text-[#9A8AA0]">Illustrative demo — actual results vary by upload.</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <h2 className="font-display text-2xl font-bold text-[#1A0F1C] sm:text-3xl lg:text-4xl">How the magic works</h2>
        <p className="mt-2 max-w-xl text-base font-medium text-[#5C4A61]">
          Three gentle steps from a paper scribble to a framed legend.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="rounded-3xl border border-[#4A235A]/10 bg-white p-7 shadow-[0_8px_32px_rgba(74,35,90,0.06)]"
            >
              <span className="grid h-12 w-12 place-items-center rounded-2xl bg-[#4A235A] text-[#F4D03F]">
                <s.icon size={22} />
              </span>
              <h3 className="mt-4 font-display text-xl font-bold text-[#4A235A]">{s.title}</h3>
              <p className="mt-2 text-sm font-medium leading-relaxed text-[#5C4A61]">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section className="mx-auto max-w-6xl px-5 pb-20 sm:px-8">
        <h2 className="font-display text-2xl font-bold text-[#1A0F1C] sm:text-3xl lg:text-4xl">Choose your legend</h2>
        <p className="mt-2 max-w-xl text-base font-medium text-[#5C4A61]">
          Start free. Pick a pack only when you're ready to download.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {packages.map((p) => (
            <PackageCard key={p.id} pkg={p} chooseHref={`/create?pkg=${p.id}`} introPrice={p.id === "portrait" ? introPrice : null} />
          ))}
        </div>
        <div className="mt-10 text-center">
          <Link
            to="/create"
            className="inline-flex min-h-[56px] items-center gap-2 rounded-full bg-[#4A235A] px-8 text-base font-extrabold text-white transition-transform hover:-translate-y-1"
          >
            <Wand2 size={20} /> Create your character
          </Link>
        </div>
      </section>

      <footer className="border-t border-[#4A235A]/10 bg-white/60">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-5 py-8 text-sm font-medium text-[#5C4A61] sm:px-8">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/" className="hover:text-[#4A235A]">Home</Link>
            <a href="#examples" className="hover:text-[#4A235A]">Examples</a>
            <Link to="/create" className="hover:text-[#4A235A]">Create a Character</Link>
            <Link to="/order" className="hover:text-[#4A235A]" data-testid="footer-order-link">Find My Order</Link>
            <a href="mailto:support@drawnintolegends.com" className="hover:text-[#4A235A]" data-testid="footer-contact">Contact</a>
          </div>
          <div className="flex flex-col items-start justify-between gap-2 border-t border-[#4A235A]/10 pt-4 sm:flex-row sm:items-center">
            <span>© {new Date().getFullYear()} Drawn Into Legend · Questions? <a href="mailto:support@drawnintolegends.com" className="font-bold text-[#4A235A] hover:underline">support@drawnintolegends.com</a></span>
            <span className="text-xs text-[#9A8AA0]">Previews are watermarked; the clean high-resolution download is included after purchase.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
